import { customerPriceAverage7d } from "./customer-price";
import { customNumberValueKey, parseCustomNumberColumnKey } from "@/lib/custom-number-columns";
import { ordersInWeek, ordersOnDate, ordersWeeksAnchorDate, parseOrdersWeekDayKey, parseOrdersWeekSumKey } from "@/lib/orders-weeks-columns";
import { adsDrr, averageOf, minOf, parseWeekCalendarKey, seriesValueOnDate, seriesWeekValues, weekDatesUntil } from "@/lib/week-calendars";
import { parseWbRegionKey } from "@/lib/wb-region-columns";
import { criticalClusterDaysLeft } from "@/lib/cluster-days-left";
import { productErrorsText } from "@/lib/product-errors";
import { CLUSTER_MATRIX_KEY, clusterMatrixRowsFromOrderData, clusterMatrixSortValue, clusterMatrixText } from "@/lib/cluster-matrix";
import { CLUSTER_DELIVERY_DAYS_METRIC, deliveryDaysForCluster, deliveryDaysText } from "@/lib/delivery-days";
import { SIZE_IDS } from "@/lib/constants";
import { buyoutCohortRate } from "./buyout-cohort";
import { orderQuantity14d } from "@/lib/product-types";
import { adKey, dataKey, getStocks, type Product, type Warehouse } from "@/lib/product-types";
import type { AdPeriodKey, AdPeriodStats } from "@/lib/performance-periods";
import { countAvailablePromotions, hasActivePromotion, promoKeys } from "@/lib/promo-presence";
import type { SortDeps } from "@/lib/sort-types";
import {
  getActiveSupplyTransitDate,
  getActiveSupplyTransitDetails,
  getActiveSupplyTransitQty,
  getReceivingSupplyDetails,
  getReceivingSupplyQty,
  getRequestedSupplyDetails,
  getRequestedSupplyQty,
  getSupplyQtyForCluster,
} from "@/lib/supply-types";
import { getTurnoverDaysToZero, getTurnoverSalesPerDay } from "@/lib/turnover-calculation";

export type ResolvedColumnValue = {
  exportValue: string | number | null;
  sortValue?: number;
  textValue?: string;
};

function sum(values: number[] | undefined): number {
  return (values ?? []).reduce((total, value) => total + value, 0);
}

function sumLast(values: number[] | undefined, count: number): number {
  return sum((values ?? []).slice(-count));
}

function firstAttr(attr: Record<string, string> | undefined, ids: readonly string[]): string {
  for (const id of ids) {
    if (attr?.[id]) return attr[id];
  }
  return "";
}

export function resolveCustomerPriceAvg7d(product: Product, deps: SortDeps): number {
  return customerPriceAverage7d(deps.orderData[dataKey(product)]);
}

export function resolveOrdersDailyTotal(product: Product, deps: SortDeps): number {
  return sum(deps.orderData[dataKey(product)]?.daily_12d);
}

function orderQty14d(stats: SortDeps["orderData"][string] | undefined): number {
  return orderQuantity14d(stats);
}

function resolveTrailingNoSalesDays(product: Product, deps: SortDeps): number {
  const daily = deps.orderData[dataKey(product)]?.daily_orders_30d;
  if (!daily || daily.length === 0) return -1;
  let count = 0;
  for (let index = daily.length - 1; index >= 0; index--) {
    if ((daily[index] ?? 0) === 0) count++;
    else break;
  }
  return count;
}

function resolveClusterDaysLeft(product: Product, deps: SortDeps, days: 7 | 14 | 30): ResolvedColumnValue {
  const stats = deps.orderData[dataKey(product)];
  // Минимум-по-кластеру — то же значение, что показывает ячейка (тултип).
  // Раньше тут был агрегат (общий запас ÷ общая скорость), из-за чего
  // сортировка/экспорт расходились с отображаемым в ячейке числом.
  const daysLeft = stats ? criticalClusterDaysLeft(product.warehouses, stats, days) : null;
  return {
    exportValue: daysLeft ?? 0,
    sortValue: daysLeft ?? 9999,
  };
}

/**
 * Календари остатков, цены и рекламы (lib/week-calendars): день — значение
 * ряда, итог недели — минимум / средняя / расход за неделю. Тот же расчёт, что
 * показывает ячейка; экспорт рекламы — расход, ДРР остаётся в ячейке.
 */
function resolveDayCalendarColumn(product: Product, key: string, deps: SortDeps): ResolvedColumnValue | null {
  const parsed = parseWeekCalendarKey(key);
  if (!parsed || parsed.metric === "orders") return null;
  const anchor = ordersWeeksAnchorDate();
  if (parsed.metric === "ads") {
    const series = deps.dayCalendars?.ads;
    const row = series ? series.values[dataKey(product)] ?? series.values[adKey(product)] : undefined;
    if (!series || !row) return { exportValue: null, sortValue: 0 };
    if (parsed.kind === "day") {
      const spend = seriesValueOnDate(row.spend, series.days, parsed.date) ?? 0;
      return { exportValue: spend, sortValue: spend };
    }
    let spend = 0, revenue = 0;
    for (const date of weekDatesUntil(parsed.date, anchor)) {
      spend += seriesValueOnDate(row.spend, series.days, date) ?? 0;
      revenue += seriesValueOnDate(row.revenue, series.days, date) ?? 0;
    }
    const drr = adsDrr(spend, revenue);
    return { exportValue: spend, sortValue: spend, textValue: drr === null ? undefined : `${spend} (ДРР ${drr.toFixed(1)}%)` };
  }
  // Нет записи — пустой экспорт и ноль в сортировке: строка «Итого» складывает
  // именно sortValue, и «−1» за каждый товар без истории портил бы сумму.
  const series = parsed.metric === "stock" ? deps.dayCalendars?.stock : deps.dayCalendars?.price;
  const values = series ? series.values[dataKey(product)] ?? series.values[adKey(product)] : undefined;
  if (!series || !values) return { exportValue: null, sortValue: 0 };
  if (parsed.kind === "day") {
    const value = seriesValueOnDate(values, series.days, parsed.date);
    return { exportValue: value, sortValue: value ?? 0 };
  }
  const week = seriesWeekValues(values, series.days, parsed.date, anchor);
  const value = parsed.metric === "stock" ? minOf(week) : averageOf(week);
  const rounded = value === null ? null : Math.round(value);
  return { exportValue: rounded, sortValue: rounded ?? 0 };
}

function resolveOrderColumn(product: Product, key: string, deps: SortDeps): ResolvedColumnValue | null {
  const stats = deps.orderData[dataKey(product)];
  // Календарь заказов по неделям: день — число из ряда, неделя — сумма её
  // прошедших дней. Тот же расчёт, что показывает ячейка.
  const weekDay = parseOrdersWeekDayKey(key);
  if (weekDay) {
    const value = ordersOnDate(stats?.daily_orders_30d, weekDay, ordersWeeksAnchorDate());
    return { exportValue: value, sortValue: value };
  }
  const weekStart = parseOrdersWeekSumKey(key);
  if (weekStart) {
    const value = ordersInWeek(stats?.daily_orders_30d, weekStart, ordersWeeksAnchorDate());
    return { exportValue: value, sortValue: value };
  }
  if (key === "orders_daily" || key === "orders_sparkline") {
    const value = resolveOrdersDailyTotal(product, deps);
    return { exportValue: value, sortValue: value };
  }
  if (key === "orders_1d") return { exportValue: stats?.qty_1d ?? 0, sortValue: stats?.qty_1d ?? 0 };
  if (key === "orders_7d") return { exportValue: stats?.qty_7d ?? 0, sortValue: stats?.qty_7d ?? 0 };
  if (key === "orders_14d") {
    const value = orderQty14d(stats);
    return { exportValue: value, sortValue: value };
  }
  if (key === "orders_30d") return { exportValue: stats?.qty_30d ?? 0, sortValue: stats?.qty_30d ?? 0 };
  // Прошлые окна заказов (для стрелки сравнения у «Цифры» на дашборде) — те же
  // qty_prev_*, что показывает колонка «Заказы N дней» в редакторе вида.
  if (key === "orders_prev_7d") return { exportValue: stats?.qty_prev_7d ?? 0, sortValue: stats?.qty_prev_7d ?? 0 };
  if (key === "orders_prev_14d") return { exportValue: stats?.qty_prev_14d ?? 0, sortValue: stats?.qty_prev_14d ?? 0 };
  if (key === "orders_prev_30d") return { exportValue: stats?.qty_prev_30d ?? 0, sortValue: stats?.qty_prev_30d ?? 0 };
  if (key === "orders_month") return { exportValue: stats?.qty_month ?? 0, sortValue: stats?.qty_month ?? 0 };
  if (key === "orders_in_transit") return { exportValue: stats?.qty_in_transit ?? 0, sortValue: stats?.qty_in_transit ?? 0 };
  if (key === "orders_revenue") return { exportValue: stats?.revenue_30d ?? 0, sortValue: stats?.revenue_30d ?? 0 };
  if (key === "orders_commission") return { exportValue: stats?.commission_30d ?? 0, sortValue: stats?.commission_30d ?? 0 };
  if (key === "orders_speed" || key === "orders_clusters_speed_7d") {
    const qty = stats?.qty_7d ?? 0;
    const value = qty ? round1(qty / 7) : 0;
    return { exportValue: value, sortValue: value };
  }
  if (key === "orders_speed_14d" || key === "orders_clusters_speed_14d") {
    const value = round1(orderQty14d(stats) / 14);
    return { exportValue: value, sortValue: value };
  }
  if (key === "orders_speed_30d" || key === "orders_clusters_speed_30d") {
    const qty = stats?.qty_30d ?? 0;
    const value = qty ? round1(qty / 30) : 0;
    return { exportValue: value, sortValue: value };
  }
  if (key === "orders_clusters") return { exportValue: stats?.qty_30d ?? 0, sortValue: stats?.qty_30d ?? 0 };
  if (key === "orders_clusters_7d") return { exportValue: stats?.qty_7d ?? 0, sortValue: stats?.qty_7d ?? 0 };
  if (key === "orders_clusters_14d") {
    const value = orderQty14d(stats);
    return { exportValue: value, sortValue: value };
  }
  if (key === "orders_share") {
    const value = stats && deps.totalOrders30d ? round1(((stats.qty_30d ?? 0) / deps.totalOrders30d) * 100) : 0;
    return { exportValue: value, sortValue: value };
  }
  if (key === "orders_share_7d") {
    const value = stats && deps.totalOrders7d ? round1(((stats.qty_7d ?? 0) / deps.totalOrders7d) * 100) : 0;
    return { exportValue: value, sortValue: value };
  }
  if (key === "days_no_sales") {
    const value = resolveTrailingNoSalesDays(product, deps);
    return { exportValue: value, sortValue: value };
  }
  if (key === "cluster_days_left_7d") return resolveClusterDaysLeft(product, deps, 7);
  if (key === "cluster_days_left_14d") return resolveClusterDaysLeft(product, deps, 14);
  if (key === "cluster_days_left_30d") return resolveClusterDaysLeft(product, deps, 30);
  if (key === "ads") {
    const value = round1(getTurnoverSalesPerDay(product, deps.orderData));
    return { exportValue: value, sortValue: value };
  }
  if (key === "idc" || key === "turnover") {
    const value = getTurnoverDaysToZero(product, deps.orderData);
    return { exportValue: value, sortValue: value ?? 0 };
  }
  return null;
}

export function resolveDailyCtr14d(product: Product): number {
  const views = product.analytics?.daily?.hits_view ?? [];
  const cardViews = product.analytics?.daily?.hits_view_pdp ?? [];
  let viewSum = 0;
  let cardViewSum = 0;
  const days = Math.max(views.length, cardViews.length);
  const start = Math.max(0, days - 14);
  for (let i = start; i < days; i++) {
    viewSum += views[i] ?? 0;
    cardViewSum += cardViews[i] ?? 0;
  }
  return viewSum > 0 ? (cardViewSum / viewSum) * 100 : 0;
}

function resolveCostPrice(product: Product, deps: SortDeps): number | null {
  const costData = deps.costData;
  if (!costData) return null;
  const scopedListingKey = product.__store?.id && product.listing_key ? `${product.__store.id}:${product.listing_key}` : null;
  return costData[dataKey(product)]
    ?? (scopedListingKey ? costData[scopedListingKey] : undefined)
    ?? (product.listing_key ? costData[product.listing_key] : undefined)
    ?? costData[product.offer_id]
    ?? null;
}

function resolveOpenTaskCount(product: Product, deps: SortDeps): number {
  const taskData = deps.taskData;
  if (!taskData) return 0;
  const listingKey = (product as Product & { listing_key?: string }).listing_key;
  const scopedListingKey = product.__store?.id && listingKey ? `${product.__store.id}:${listingKey}` : null;
  const tasks = taskData[dataKey(product)]
    ?? (scopedListingKey ? taskData[scopedListingKey] : undefined)
    ?? taskData[product.offer_id]
    ?? (listingKey ? taskData[listingKey] : undefined)
    ?? [];
  return tasks.filter(task => task.status === "open").length;
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

function parseNumber(value: number | string | null | undefined): number | null {
  if (value == null || value === "") return null;
  const parsed = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function activeServiceTypes(product: Product): string {
  return (product.availabilities ?? [])
    .filter(item => item.availability === "AVAILABLE")
    .map(item => item.source?.toUpperCase())
    .filter(Boolean)
    .join("/");
}

function vatLabel(value: string | null | undefined): string {
  const map: Record<string, string> = { "0": "0%", "0.05": "5%", "0.1": "10%", "0.15": "15%", "0.2": "20%" };
  return map[value ?? ""] ?? (value ?? "");
}

function resolveProductColumn(product: Product, key: string): ResolvedColumnValue | null {
  if (key === "photo" || key === "primary_image") {
    const value = product.primary_image?.[0] ?? "";
    return { exportValue: value, textValue: value };
  }
  if (key === "name") {
    const value = product.name ?? "";
    return { exportValue: value, textValue: value };
  }
  if (key === "offer_id") {
    const value = product.offer_id ?? "";
    return { exportValue: value, textValue: value };
  }
  if (key === "product_id") {
    const value = product.id == null ? "" : String(product.id);
    return { exportValue: value, sortValue: product.id ?? 0, textValue: value };
  }
  if (key === "status") {
    const value = product.statuses?.status_name ?? "";
    return { exportValue: value, textValue: value };
  }
  if (key === "price") {
    const value = parseNumber(product.price);
    return { exportValue: value, sortValue: value ?? 0 };
  }
  if (key === "old_price") {
    const value = product.old_price && product.old_price !== "0" ? parseNumber(product.old_price) : null;
    return { exportValue: value, sortValue: value ?? 0 };
  }
  if (key === "description_category_id") {
    const value = product.category_name ?? (product.description_category_id != null ? String(product.description_category_id) : "");
    return { exportValue: value, textValue: value };
  }
  if (key === "type_id") {
    const value = product.type_name ?? (product.type_id != null ? String(product.type_id) : "");
    return { exportValue: value, textValue: value };
  }
  if (key === "barcodes") {
    const barcodes = product.barcodes ?? [];
    return { exportValue: barcodes.join(", "), textValue: barcodes.length ? barcodes[barcodes.length - 1] : "" };
  }
  if (key === "sources") {
    const sources = product.sources ?? [];
    return { exportValue: sources.map(source => source.source?.toUpperCase()).filter(Boolean).join("/"), sortValue: sources.length };
  }
  if (key === "errors") {
    const value = product.errors?.length ?? 0;
    return { exportValue: value, sortValue: value };
  }
  if (key === "errors_text") {
    const value = productErrorsText(product.errors);
    return { exportValue: value, textValue: value };
  }
  if (key === "vat") {
    const value = vatLabel(product.vat);
    return { exportValue: value, sortValue: parseNumber(product.vat) ?? 0, textValue: value };
  }
  if (key === "service_type") {
    const value = activeServiceTypes(product);
    return { exportValue: value, textValue: value };
  }
  if (key === "is_kgt") {
    const value = product.is_kgt ? 1 : 0;
    return { exportValue: value ? "КГТ" : "", sortValue: value };
  }
  if (key === "is_prepayment") {
    const value = product.is_prepayment_allowed ? 1 : 0;
    return { exportValue: value ? "Да" : "", sortValue: value };
  }
  if (key === "has_discounted_item") {
    const value = product.has_discounted_fbo_item ? 1 : 0;
    return { exportValue: value ? "Есть" : "", sortValue: value };
  }
  if (key === "is_discounted") {
    const value = product.is_discounted ? 1 : 0;
    return { exportValue: value ? "Уценка" : "", sortValue: value };
  }
  if (key === "super") {
    const value = product.is_super ? 1 : 0;
    return { exportValue: value ? "Супер" : "", sortValue: value };
  }
  return null;
}

function analyticsNumber(product: Product, pick: (analytics: NonNullable<Product["analytics"]>) => number | null | undefined): number {
  const value = product.analytics ? pick(product.analytics) : 0;
  return Number(value) || 0;
}

function resolveAnalyticsColumn(product: Product, key: string, deps: SortDeps): ResolvedColumnValue | null {
  if (key === "revenue") {
    const value = deps.financeData?.[dataKey(product)]?.revenue ?? product.analytics?.revenue ?? 0;
    return { exportValue: value, sortValue: value };
  }
  if (key === "ordered_units") {
    const value = analyticsNumber(product, analytics => analytics.ordered_units);
    return { exportValue: value, sortValue: value };
  }
  if (key === "delivered") {
    const value = analyticsNumber(product, analytics => analytics.delivered_units);
    return { exportValue: value, sortValue: value };
  }
  if (key === "returns") {
    const value = analyticsNumber(product, analytics => analytics.returns);
    return { exportValue: value, sortValue: value };
  }
  if (key === "cancellations") {
    const value = analyticsNumber(product, analytics => analytics.cancellations);
    return { exportValue: value, sortValue: value };
  }
  if (key === "position") {
    const value = product.analytics?.position_category ?? null;
    return { exportValue: value, sortValue: value ?? 0 };
  }
  if (key === "hits_view") {
    const value = analyticsNumber(product, analytics => analytics.hits_view);
    return { exportValue: value, sortValue: value };
  }
  if (key === "hits_tocart") {
    const value = analyticsNumber(product, analytics => analytics.hits_tocart);
    return { exportValue: value, sortValue: value };
  }
  if (key === "hits_tocart_search") {
    const value = analyticsNumber(product, analytics => analytics.hits_tocart_search);
    return { exportValue: value, sortValue: value };
  }
  if (key === "hits_tocart_pdp") {
    const value = analyticsNumber(product, analytics => analytics.hits_tocart_pdp);
    return { exportValue: value, sortValue: value };
  }
  if (key === "hits_search") {
    const value = analyticsNumber(product, analytics => analytics.hits_view_search);
    return { exportValue: value, sortValue: value };
  }
  if (key === "hits_pdp") {
    const value = analyticsNumber(product, analytics => analytics.hits_view_pdp);
    return { exportValue: value, sortValue: value };
  }
  if (key === "conv_tocart") {
    const value = analyticsNumber(product, analytics => analytics.conv_tocart);
    return { exportValue: value, sortValue: value };
  }
  if (key === "conv_search") {
    const value = analyticsNumber(product, analytics => analytics.conv_tocart_search);
    return { exportValue: value, sortValue: value };
  }
  if (key === "conv_pdp") {
    const value = analyticsNumber(product, analytics => analytics.conv_tocart_pdp);
    return { exportValue: value, sortValue: value };
  }
  if (key === "session_view") {
    const value = analyticsNumber(product, analytics => analytics.session_view);
    return { exportValue: value, sortValue: value };
  }
  if (key === "session_search") {
    const value = analyticsNumber(product, analytics => analytics.session_view_search);
    return { exportValue: value, sortValue: value };
  }
  if (key === "session_pdp") {
    const value = analyticsNumber(product, analytics => analytics.session_view_pdp);
    return { exportValue: value, sortValue: value };
  }
  if (key === "return_rate") {
    const delivered = product.analytics?.delivered_units ?? 0;
    const returns = product.analytics?.returns ?? 0;
    const value = delivered > 0 ? round1((returns / delivered) * 100) : 0;
    return { exportValue: value, sortValue: value };
  }
  if (key === "revenue_per_unit") {
    const units = product.analytics?.ordered_units ?? 0;
    const revenue = deps.financeData?.[dataKey(product)]?.revenue ?? product.analytics?.revenue ?? 0;
    const value = units ? Math.round(revenue / units) : null;
    return { exportValue: value, sortValue: value ?? 0 };
  }
  return null;
}

function resolvePriceColumn(product: Product, key: string, deps: SortDeps): ResolvedColumnValue | null {
  const price = deps.priceData?.[dataKey(product)];
  if (key === "price_min") return { exportValue: price?.min_price ?? null, sortValue: price?.min_price ?? 0 };
  if (key === "price_marketing") return { exportValue: price?.marketing_price ?? null, sortValue: price?.marketing_price ?? 0 };
  if (key === "price_seller_promo") return { exportValue: price?.marketing_seller_price ?? null, sortValue: price?.marketing_seller_price ?? 0 };
  if (key === "price_retail") return { exportValue: price?.retail_price ?? null, sortValue: price?.retail_price ?? 0 };
  if (key === "ozon_min_competitor") return { exportValue: price?.ozon_min_competitor ?? null, sortValue: price?.ozon_min_competitor ?? 0 };
  if (key === "ext_min_price") {
    const value = parseNumber(price?.ext_min_price);
    return { exportValue: value, sortValue: value ?? 0 };
  }
  if (key === "commission_fbo") return { exportValue: price?.commission_fbo ?? null, sortValue: price?.commission_fbo ?? 0 };
  if (key === "commission_fbs") return { exportValue: price?.commission_fbs ?? null, sortValue: price?.commission_fbs ?? 0 };
  if (key === "price_index") {
    const value = price?.color_index ?? "";
    return { exportValue: value, textValue: value };
  }
  if (key === "ozon_index_value") return { exportValue: price?.ozon_index_value ?? null, sortValue: price?.ozon_index_value ?? 0 };
  if (key === "ext_index_value") return { exportValue: price?.ext_index_value ?? null, sortValue: price?.ext_index_value ?? 0 };
  if (key === "volume_weight") return { exportValue: price?.volume_weight ?? null, sortValue: price?.volume_weight ?? 0 };
  if (key === "has_promotion") {
    const value = price?.has_promotion ? 1 : 0;
    return { exportValue: value ? "Да" : "", sortValue: value };
  }
  if (key === "promo_presence") {
    const value = hasActivePromotion(product, deps.promoData);
    return { exportValue: value ? "Да" : "", sortValue: value ? 1 : 0 };
  }
  if (key === "promo_available") {
    const value = countAvailablePromotions(product, deps.promoData, price);
    return { exportValue: value || "", sortValue: value };
  }
  if (key === "promo_from") {
    const value = price?.promo_from ?? "";
    return { exportValue: value, textValue: value };
  }
  if (key === "promo_to") {
    const value = price?.promo_to ?? "";
    return { exportValue: value, textValue: value };
  }
  return null;
}

function resolveFinanceColumn(product: Product, key: string, deps: SortDeps): ResolvedColumnValue | null {
  const finance = deps.financeData?.[dataKey(product)];
  if (key === "fin_buyout_count") return { exportValue: finance?.buyoutCount ?? 0, sortValue: finance?.buyoutCount ?? 0 };
  if (key === "fin_revenue") return { exportValue: finance?.revenue ?? 0, sortValue: finance?.revenue ?? 0 };
  if (key === "fin_commission") return { exportValue: finance?.commission ?? 0, sortValue: finance?.commission ?? 0 };
  if (key === "fin_logistics") return { exportValue: finance?.logistics ?? 0, sortValue: finance?.logistics ?? 0 };
  if (key === "fin_last_mile") return { exportValue: finance?.lastMile ?? 0, sortValue: finance?.lastMile ?? 0 };
  if (key === "fin_fulfillment") return { exportValue: finance?.fulfillment ?? 0, sortValue: finance?.fulfillment ?? 0 };
  if (key === "fin_mainline") return { exportValue: finance?.mainline ?? 0, sortValue: finance?.mainline ?? 0 };
  if (key === "fin_return_count") return { exportValue: finance?.returnCount ?? 0, sortValue: finance?.returnCount ?? 0 };
  if (key === "fin_return_amount") return { exportValue: finance?.returnAmount ?? 0, sortValue: finance?.returnAmount ?? 0 };
  if (key === "fin_delivered_14d") {
    const value = sumLast(finance?.dailyBuyouts, 14) + sumLast(finance?.dailyCancels, 14);
    return { exportValue: value, sortValue: value };
  }
  if (key === "fin_buyout_14d") {
    const value = sumLast(finance?.dailyBuyouts, 14);
    return { exportValue: value, sortValue: value };
  }
  if (key === "fin_cancel_14d") {
    const value = sumLast(finance?.dailyCancels, 14);
    return { exportValue: value, sortValue: value };
  }
  if (key === "fin_late_return_14d") {
    const value = sumLast(finance?.dailyReturns, 14);
    return { exportValue: value, sortValue: value };
  }
  if (key === "fin_buyout_rate") {
    const value = buyoutCohortRate(finance?.buyoutCohort14d);
    return { exportValue: value, sortValue: value ?? NaN };
  }
  return null;
}

function sumWarehouses(product: Product, pick: (warehouse: Warehouse) => number | null | undefined): number {
  return (product.warehouses ?? []).reduce((total, warehouse) => total + (pick(warehouse) ?? 0), 0);
}

const WAREHOUSE_TURNOVER_GRADE_VALUE: Record<string, number> = { A: 1, B: 2, C: 3, D: 4 };

function resolveWarehouseTurnover(product: Product): ResolvedColumnValue {
  const grades = (product.warehouses ?? []).map(warehouse => warehouse.turnover_grade).filter(Boolean);
  if (!grades.length) return { exportValue: "", sortValue: 0, textValue: "" };
  const worst = grades.reduce((current, grade) => {
    return (WAREHOUSE_TURNOVER_GRADE_VALUE[grade] ?? 0) > (WAREHOUSE_TURNOVER_GRADE_VALUE[current] ?? 0) ? grade : current;
  }, grades[0]);
  return {
    exportValue: worst,
    sortValue: WAREHOUSE_TURNOVER_GRADE_VALUE[worst] ?? 0,
    textValue: worst,
  };
}

function resolveStockColumn(product: Product, key: string): ResolvedColumnValue | null {
  if (key === "stock") {
    const value = getStocks(product).present;
    return { exportValue: value, sortValue: value };
  }
  if (key === "reserved") {
    const value = getStocks(product).reserved;
    return { exportValue: value, sortValue: value };
  }
  if (key === "days_without_sales") {
    const value = product.warehouses?.length ? Math.max(...product.warehouses.map(warehouse => warehouse.days_without_sales ?? 0)) : null;
    return { exportValue: value, sortValue: value ?? 0 };
  }
  if (key === "stock_in_transit") {
    const value = sumWarehouses(product, warehouse => warehouse.in_transit);
    return { exportValue: value, sortValue: value };
  }
  // WB: в пути к/от клиента (из warehouse_remains, лежит в warehouses как отдельные
  // записи). Только для WB — у Ozon in_transit/return_customer значат другое.
  if (key === "wb_in_transit_to_client") {
    const value = product.marketplace === "wb" ? sumWarehouses(product, warehouse => warehouse.in_transit) : 0;
    return { exportValue: value, sortValue: value };
  }
  if (key === "wb_in_transit_from_client") {
    const value = product.marketplace === "wb" ? sumWarehouses(product, warehouse => warehouse.return_customer) : 0;
    return { exportValue: value, sortValue: value };
  }
  if (key === "valid_stock") {
    const value = sumWarehouses(product, warehouse => warehouse.valid);
    return { exportValue: value, sortValue: value };
  }
  if (key === "excess_stock") {
    const value = sumWarehouses(product, warehouse => warehouse.excess);
    return { exportValue: value, sortValue: value };
  }
  if (key === "expiring_stock") {
    const value = sumWarehouses(product, warehouse => warehouse.expiring);
    return { exportValue: value, sortValue: value };
  }
  if (key === "stock_defect") {
    const value = sumWarehouses(product, warehouse => warehouse.defect);
    return { exportValue: value, sortValue: value };
  }
  if (key === "transit_defect_stock") {
    const value = sumWarehouses(product, warehouse => warehouse.transit_defect);
    return { exportValue: value, sortValue: value };
  }
  if (key === "return_customer_stock") {
    const value = sumWarehouses(product, warehouse => warehouse.return_customer);
    return { exportValue: value, sortValue: value };
  }
  if (key === "return_seller_stock") {
    const value = sumWarehouses(product, warehouse => warehouse.return_seller);
    return { exportValue: value, sortValue: value };
  }
  if (key === "waiting_docs_stock") {
    const value = sumWarehouses(product, warehouse => warehouse.waiting_docs);
    return { exportValue: value, sortValue: value };
  }
  if (key === "requested_stock") {
    const value = sumWarehouses(product, warehouse => warehouse.requested);
    return { exportValue: value, sortValue: value };
  }
  if (key === "other_stock") {
    const value = sumWarehouses(product, warehouse => warehouse.other);
    return { exportValue: value, sortValue: value };
  }
  if (key === "warehouse_turnover") return resolveWarehouseTurnover(product);
  if (key === "warehouse_ads") {
    const value = round1(sumWarehouses(product, warehouse => warehouse.ads));
    return { exportValue: value, sortValue: value };
  }
  return null;
}

function resolveSupplyColumn(product: Product, key: string, deps: SortDeps): ResolvedColumnValue | null {
  const supply = deps.supplyData?.[dataKey(product)];
  if (key === "supply_last_date") {
    const value = supply?.lastDate ?? "";
    return { exportValue: value, textValue: value };
  }
  if (key === "supply_last_qty") return { exportValue: supply?.lastQty ?? 0, sortValue: supply?.lastQty ?? 0 };
  if (key === "supply_days_since") return { exportValue: supply?.daysSinceLast ?? null, sortValue: supply?.daysSinceLast ?? 0 };
  if (key === "supply_count_30d") return { exportValue: supply?.count30d ?? 0, sortValue: supply?.count30d ?? 0 };
  if (key === "supply_count_90d") return { exportValue: supply?.count90d ?? 0, sortValue: supply?.count90d ?? 0 };
  if (key === "supply_total_30d") return { exportValue: supply?.total30d ?? 0, sortValue: supply?.total30d ?? 0 };
  if (key === "supply_total_90d") return { exportValue: supply?.total90d ?? 0, sortValue: supply?.total90d ?? 0 };
  if (key === "supply_order_in_transit") {
    const value = getRequestedSupplyQty(supply);
    return { exportValue: value, sortValue: value };
  }
  if (key === "supply_in_transit_qty") {
    const value = getActiveSupplyTransitQty(supply);
    return { exportValue: value, sortValue: value };
  }
  if (key === "supply_receiving_qty") {
    const value = getReceivingSupplyQty(supply);
    return { exportValue: value, sortValue: value };
  }
  if (key === "supply_in_transit_date") {
    const value = getActiveSupplyTransitDate(supply) ?? "";
    return { exportValue: value, textValue: value };
  }
  if (key === "supply_cluster") {
    const value = supply?.lastWarehouse ?? "";
    return { exportValue: value, textValue: value };
  }
  return null;
}

function attrValue(product: Product, deps: SortDeps, ids: readonly string[]): string {
  return firstAttr(deps.attrData[dataKey(product)], ids);
}

function resolveAttributeColumn(product: Product, key: string, deps: SortDeps): ResolvedColumnValue | null {
  // Числовые габариты: экспорт/сортировка числом (значение в attrData — строка).
  if (key === "attr_volume_l" || key === "attr_weight") {
    const raw = attrValue(product, deps, [key === "attr_volume_l" ? "volume_l" : "weight"]);
    const num = parseFloat(raw);
    return { exportValue: Number.isFinite(num) ? num : null, sortValue: Number.isFinite(num) ? num : 0 };
  }
  let value: string | null = null;
  if (key === "attr_sku") value = attrValue(product, deps, ["_sku"]);
  else if (key === "attr_dimensions") value = attrValue(product, deps, ["dimensions"]);
  else if (key === "attr_product_id") value = attrValue(product, deps, ["_product_id"]);
  else if (key === "attr_brand") value = attrValue(product, deps, ["85", "31"]);
  else if (key === "attr_material") value = attrValue(product, deps, ["10550", "22385"]);
  else if (key === "attr_color") value = attrValue(product, deps, ["10096"]);
  else if (key === "attr_custom_color") value = attrValue(product, deps, ["10097"]);
  else if (key === "attr_size") value = attrValue(product, deps, SIZE_IDS) || product.group_size_value?.trim() || "";
  else if (key === "attr_gender") value = attrValue(product, deps, ["9163"]);
  else if (key === "attr_season") value = attrValue(product, deps, ["9725", "4495"]);
  else if (key === "attr_common_article") value = attrValue(product, deps, ["8292", "9024"]);
  else if (key === "attr_country") value = attrValue(product, deps, ["4385"]);
  else return null;
  return { exportValue: value, textValue: value };
}

export function parseAdPeriodColumnKey(key: string): { metric: keyof AdPeriodStats; period: AdPeriodKey } | null {
  const match = key.match(/^ad_(.+)_(7d|14d|30d)$/);
  if (!match) return null;
  const metricMap: Record<string, keyof AdPeriodStats> = {
    spend: "spend",
    views: "views",
    clicks: "clicks",
    ctr: "ctr",
    cpc: "cpc",
    tocart: "toCart",
    orders: "orders",
    model_orders: "modelOrders",
    total_orders: "totalOrders",
    revenue: "revenue",
    model_revenue: "modelRevenue",
    total_revenue: "totalRevenue",
    conv_cart: "convCart",
    conv_order: "convOrder",
    cost_per_order: "costPerOrder",
    drr: "drr",
    total_drr: "totalDrr",
    product_gmv: "productGmv",
    shop_drr: "shopDrr",
    avg_bid: "avgBid",
  };
  const metric = metricMap[match[1]];
  if (!metric) return null;
  const period: AdPeriodKey = match[2] === "7d" ? "d7" : match[2] === "14d" ? "d14" : "d30";
  return { metric, period };
}

function resolveAdColumn(product: Product, key: string, deps: SortDeps): ResolvedColumnValue | null {
  if (key === "ad_presence") {
    const hasAds = deps.adActiveKeys
      ? deps.adActiveKeys.has(adKey(product))
      : (() => {
          const stats = deps.adPeriodData?.[adKey(product)]?.d30;
          return !!stats && (stats.spend > 0 || stats.views > 0 || stats.clicks > 0 || stats.totalOrders > 0 || stats.totalRevenue > 0);
        })();
    return { exportValue: hasAds ? "Да" : "", sortValue: hasAds ? 1 : 0 };
  }

  const parsed = parseAdPeriodColumnKey(key);
  if (!parsed) return null;
  const value = deps.adPeriodData?.[adKey(product)]?.[parsed.period]?.[parsed.metric] ?? 0;
  return { exportValue: value, sortValue: value };
}

function resolveClusterColumn(product: Product, key: string, deps: SortDeps): ResolvedColumnValue | null {
  const sep = key.indexOf(":");
  if (sep === -1) return null;
  const metric = key.slice(0, sep);
  const cluster = key.slice(sep + 1);
  const stats = deps.orderData[dataKey(product)];
  const supplyStats = deps.supplyData?.[dataKey(product)];
  const clusterMap = deps.clusterMap ?? {};
  const warehouses = (product.warehouses ?? []).filter(warehouse => warehouse.cluster === cluster);

  if (metric === "cluster_stock") {
    const value = warehouses.reduce((total, warehouse) => total + (warehouse.free ?? 0) + (warehouse.valid ?? 0), 0);
    return { exportValue: value, sortValue: value };
  }
  // Срок доставки: сортировка по общему среднему; без доставок — в конец.
  if (metric === CLUSTER_DELIVERY_DAYS_METRIC) {
    const summary = deliveryDaysForCluster([deps.deliveryDaysData?.[dataKey(product)]], cluster);
    const text = deliveryDaysText(summary);
    return { exportValue: text || null, sortValue: summary.all ?? 9999, textValue: text || undefined };
  }
  if (metric === "cluster_supply_request") {
    const value = getSupplyQtyForCluster(getRequestedSupplyDetails(supplyStats), cluster, clusterMap);
    return { exportValue: value, sortValue: value };
  }
  if (metric === "cluster_in_transit") {
    const value = getSupplyQtyForCluster(getActiveSupplyTransitDetails(supplyStats), cluster, clusterMap);
    return { exportValue: value, sortValue: value };
  }
  if (metric === "cluster_receiving") {
    const value = getSupplyQtyForCluster(getReceivingSupplyDetails(supplyStats), cluster, clusterMap);
    return { exportValue: value, sortValue: value };
  }
  if (metric === "cluster_orders") {
    const value = stats?.clusters_to[cluster] ?? 0;
    return { exportValue: value, sortValue: value };
  }
  if (metric === "cluster_orders_14d") {
    const value = stats?.clusters_to_14d?.[cluster] ?? 0;
    return { exportValue: value, sortValue: value };
  }
  if (metric === "cluster_orders_7d") {
    const value = stats?.clusters_to_7d?.[cluster] ?? 0;
    return { exportValue: value, sortValue: value };
  }
  if (metric === "cluster_shipped") {
    const value = stats?.clusters_from[cluster] ?? 0;
    return { exportValue: value, sortValue: value };
  }
  if (metric === "cluster_shipped_14d") {
    const value = stats?.clusters_from_14d?.[cluster] ?? 0;
    return { exportValue: value, sortValue: value };
  }
  if (metric === "cluster_shipped_7d") {
    const value = stats?.clusters_from_7d?.[cluster] ?? 0;
    return { exportValue: value, sortValue: value };
  }
  if (metric === "cluster_speed") {
    const total30 = stats?.clusters_to[cluster] ?? 0;
    if (!total30 || !stats?.qty_30d) return { exportValue: 0, sortValue: 0 };
    const value = (total30 * stats.qty_7d / stats.qty_30d) / 7;
    return { exportValue: round1(value), sortValue: value };
  }
  if (metric === "cluster_share") {
    const clusterOrders = stats?.clusters_to[cluster] ?? 0;
    const totalOrders = stats?.qty_30d ?? 0;
    const value = totalOrders ? (clusterOrders / totalOrders) * 100 : 0;
    return { exportValue: round1(value), sortValue: value };
  }
  if (metric === "cluster_shipped_share") {
    const shipped = stats?.clusters_from[cluster] ?? 0;
    const total = Object.values(stats?.clusters_from ?? {}).reduce((sumValue, value) => sumValue + value, 0);
    const value = total ? (shipped / total) * 100 : 0;
    return { exportValue: round1(value), sortValue: value };
  }
  if (metric === "cluster_shipped_days_left") {
    const stock = warehouses.reduce((total, warehouse) => total + (warehouse.free ?? 0) + (warehouse.valid ?? 0), 0);
    const speed = (stats?.clusters_from_7d?.[cluster] ?? 0) / 7;
    if (speed <= 0) return { exportValue: null, sortValue: 9999 };
    const value = stock / speed;
    return { exportValue: Math.floor(value), sortValue: value };
  }

  return null;
}

function resolvePromoColumn(product: Product, key: string, deps: SortDeps): ResolvedColumnValue | null {
  const sep = key.indexOf(":");
  if (sep === -1) return null;
  const metric = key.slice(0, sep);
  const actionId = key.slice(sep + 1);
  const promoData = deps.promoData;
  if (!promoData) return null;

  const keys = promoKeys(product);
  const stats = keys.map(promoKey => promoData.byOffer?.[promoKey]?.[actionId]).find(Boolean);
  const candidatePrice = keys.map(promoKey => promoData.candidatePrices?.[actionId]?.[promoKey]).find(price => price != null);

  if (metric === "promo_participating") {
    const value = stats?.is_participating
      ? 2
      : keys.some(promoKey => promoData.candidates?.[actionId]?.includes(promoKey))
        ? 1
        : 0;
    const label = value === 2 ? "Участвует" : value === 1 ? "Доступно" : "Недоступно";
    return { exportValue: label, sortValue: value, textValue: label };
  }
  if (metric === "promo_action_price") {
    const value = stats?.action_price || candidatePrice || 0;
    return { exportValue: value || null, sortValue: value };
  }
  if (metric === "promo_discount_pct") {
    const actionPrice = stats?.action_price || candidatePrice;
    const currentPrice = parseFloat(product.price);
    const value = actionPrice && currentPrice ? Math.round((1 - actionPrice / currentPrice) * 100) : 0;
    return { exportValue: actionPrice ? value : null, sortValue: value };
  }
  // Составная колонка одной акции (promo_action:<id>): участие + цена по акции +
  // скидка % одной строкой. Не сортируется (sortValue=0) — id акций меняются.
  if (metric === "promo_action") {
    const isCandidate = keys.some(promoKey => promoData.candidates?.[actionId]?.includes(promoKey));
    // Сортировка по участию: участвует (2) выше доступных (1) выше прочих (0).
    const participationRank = stats?.is_participating ? 2 : isCandidate ? 1 : 0;
    const participating = stats?.is_participating ? "Участвует" : isCandidate ? "Доступно" : "Недоступно";
    const actionPrice = stats?.action_price || candidatePrice || 0;
    const currentPrice = parseFloat(product.price);
    const discount = actionPrice && currentPrice ? Math.round((1 - actionPrice / currentPrice) * 100) : 0;
    const parts = [participating];
    if (actionPrice) parts.push(`${Math.round(actionPrice)} ₽`);
    if (actionPrice && discount) parts.push(`${discount}%`);
    const text = parts.join(" · ");
    return { exportValue: text, sortValue: participationRank, textValue: text };
  }

  return null;
}

/**
 * Кэш посчитанных значений колонок.
 *
 * Зачем. Одно и то же значение спрашивают несколько раз за отрисовку: сама
 * ячейка, сортировка по этой колонке, строка «Итого», шкала тепловой карты,
 * фильтр и меню колонки. Каждый раз оно считалось заново.
 *
 * Почему из него НЕ может прийти устаревшее число. Кэш живёт не сам по себе, а
 * внутри объекта зависимостей (`deps.__valueCache`) и создаётся вместе с ним
 * (`withSortValueCache`). Приходят новые данные — заказы, цены, реклама,
 * каталог — собирается новый `deps`, а с ним новый пустой кэш; старый умирает
 * вместе с данными, из которых был посчитан. Отдельной команды «сбросить кэш»
 * нет намеренно: сбрасывать нечего, пока кэш не переживает свои данные.
 *
 * Потолок. Значений бывает много: 2777 строк × 96 колонок ≈ 266 тыс. записей.
 * На лимите кэш очищается целиком — это дешевле и предсказуемее, чем вытеснять
 * по одной записи, а промах стоит ровно один пересчёт.
 */
export const COLUMN_VALUE_CACHE_LIMIT = 200_000;

/** Ключ записи кэша: колонка + строка. Один на все три карты кэша. */
export function columnValueCacheKey(product: Product, key: string): string {
  return `${key}\u0001${dataKey(product)}\u0001${product.id}`;
}

/** Запомнить значение, не давая кэшу расти без предела. */
export function rememberCachedValue<T>(cache: Map<string, T>, key: string, value: T): T {
  if (cache.size >= COLUMN_VALUE_CACHE_LIMIT) cache.clear();
  cache.set(key, value);
  return value;
}

export function resolveColumnValue(product: Product, key: string, deps: SortDeps): ResolvedColumnValue | null {
  const cache = deps.__valueCache;
  if (!cache) return resolveColumnValueUncached(product, key, deps);
  const cacheKey = columnValueCacheKey(product, key);
  const cached = cache.resolved.get(cacheKey);
  if (cached !== undefined) return cached;
  return rememberCachedValue(cache.resolved, cacheKey, resolveColumnValueUncached(product, key, deps));
}

function resolveColumnValueUncached(product: Product, key: string, deps: SortDeps): ResolvedColumnValue | null {
  const productColumn = resolveProductColumn(product, key);
  if (productColumn) return productColumn;

  const analyticsColumn = resolveAnalyticsColumn(product, key, deps);
  if (analyticsColumn) return analyticsColumn;

  const customNumberColumnId = parseCustomNumberColumnKey(key);
  if (customNumberColumnId) {
    const value = deps.customNumberValues?.[customNumberValueKey(customNumberColumnId, dataKey(product))] ?? null;
    return { exportValue: value, sortValue: value ?? 0 };
  }

  if (key === "cost_price") {
    const value = resolveCostPrice(product, deps);
    return { exportValue: value, sortValue: value ?? 0 };
  }
  if (key === "task") {
    const value = resolveOpenTaskCount(product, deps);
    return { exportValue: value, sortValue: value };
  }
  // WB-колонки на Ozon-строке (часть 4): данные связанного WB-размера из deps.wbLinkedData.
  // Нет пары WB у размера → пусто («—»), сортируется вниз.
  if (key === "wb_stock" || key === "wb_price" || key === "wb_ordered_7d" || key === "wb_ordered_30d" || key === "wb_in_transit_client" || key === "wb_in_transit_return") {
    const st = deps.wbLinkedData?.[dataKey(product)];
    const value = st
      ? key === "wb_stock" ? st.stock
        : key === "wb_price" ? st.price
        : key === "wb_ordered_7d" ? st.ordered7d
        : key === "wb_ordered_30d" ? st.ordered30d
        : key === "wb_in_transit_client" ? st.inTransitToClient
        : st.inTransitFromClient
      : null;
    return { exportValue: value ?? "", sortValue: value ?? -1 };
  }
  // Доля WB в заказах: WB 30д / (WB+Ozon) 30д, %. Нет пары WB → пусто.
  if (key === "wb_order_share") {
    const st = deps.wbLinkedData?.[dataKey(product)];
    if (!st) return { exportValue: "", sortValue: -1 };
    const wb = st.ordered30d;
    const oz = deps.orderData[dataKey(product)]?.qty_30d ?? 0;
    const total = wb + oz;
    const share = total > 0 ? Math.round((wb / total) * 100) : 0;
    return { exportValue: share, sortValue: share };
  }
  // Суммарный остаток Ozon + WB (если у размера есть пара WB — прибавляем её остаток).
  if (key === "total_stock") {
    const oz = getStocks(product).present;
    const wb = deps.wbLinkedData?.[dataKey(product)]?.stock ?? 0;
    const value = oz + wb;
    return { exportValue: value, sortValue: value };
  }
  // WB-колонки по регионам (wb_region_<metric>:<region>) — как кластеры Ozon,
  // но значение из wbLinkedData.stockByRegion / ordersByRegion*. Нет пары WB →
  // пусто; пара есть, но в регионе 0 → 0.
  if (key.startsWith("wb_region_")) {
    const parsed = parseWbRegionKey(key);
    if (parsed) {
      const st = deps.wbLinkedData?.[dataKey(product)];
      if (!st) return { exportValue: "", sortValue: -1 };
      const map = parsed.metric === "wb_region_stock" ? st.stockByRegion
        : parsed.metric === "wb_region_orders_7d" ? st.ordersByRegion7d
        : st.ordersByRegion30d;
      const value = map[parsed.region] ?? 0;
      return { exportValue: value, sortValue: value };
    }
  }

  const orderColumn = resolveOrderColumn(product, key, deps);
  if (orderColumn) return orderColumn;

  const priceColumn = resolvePriceColumn(product, key, deps);
  if (priceColumn) return priceColumn;

  const financeColumn = resolveFinanceColumn(product, key, deps);
  if (financeColumn) return financeColumn;

  const stockColumn = resolveStockColumn(product, key);
  if (stockColumn) return stockColumn;

  const supplyColumn = resolveSupplyColumn(product, key, deps);
  if (supplyColumn) return supplyColumn;

  const attributeColumn = resolveAttributeColumn(product, key, deps);
  if (attributeColumn) return attributeColumn;

  if (key === "customer_price_avg_7d" || key === "customer_price_chart") {
    const value = resolveCustomerPriceAvg7d(product, deps);
    return { exportValue: value, sortValue: value };
  }

  if (key === "ctr_daily_30d") {
    const value = resolveDailyCtr14d(product);
    return { exportValue: value, sortValue: value };
  }

  const dayCalendarColumn = resolveDayCalendarColumn(product, key, deps);
  if (dayCalendarColumn) return dayCalendarColumn;

  // Матрица кластеров: в сортировке — сколько кластеров заказывают без
  // остатка, в экспорте — чипы текстом.
  if (key === CLUSTER_MATRIX_KEY) {
    const rows = clusterMatrixRowsFromOrderData([product], deps.orderData);
    const text = clusterMatrixText(rows);
    return { exportValue: text, sortValue: clusterMatrixSortValue(rows), textValue: text };
  }

  if (key === "ad_presence" || key.startsWith("ad_")) return resolveAdColumn(product, key, deps);
  if (key.startsWith("cluster_")) return resolveClusterColumn(product, key, deps);
  if (key.startsWith("promo_")) return resolvePromoColumn(product, key, deps);

  return null;
}
